import { useEffect } from 'react';

import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useCartContents } from '@zenbusiness/application-commons-ecommerce';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Empty from '@zenbusiness/zui/Empty';

import AUTH_POLICY from '#client/constants/authPolicy';
import { DASHBOARD } from '#client/constants/routes';
import useFlowConfig from '#client/hooks/flow/useFlowConfig';
import useFlowState from '#client/hooks/flow/useFlowState';
import useAuthRedirectHandler from '#client/hooks/useAuthRedirectHandler';
import * as pageUtils from '#client/utils/flowPage';

/*
  Things Flow Gate component must enforce:

  1. Authenticated users must be redirected to the authRedirect path.
  2. Unauthenticated users must be redirected to the login page.
  3. If flow hash is a mismatch, then redirect to start over in flow.
  4. If cart uuid is a mismatch, then redirect to start over in flow.
  5. If user lacks required entitlements, redirect to dashboard.

  Update flowState cartUuid and hash if they have changed
*/

export default function FlowGate({ children }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { authenticated, login } = useAuthentication();
  const { entitlements = new Map() } = useBusinessEntity() ?? {};
  const { cartUuid: currentCartUuid } = useCartContents();
  const { flowPage } = useParams();
  const captureException = useCaptureException();
  const {
    id: flowId,
    authRedirect: authRedirectPath,
    authRedirectOverrides = {},
    authPolicy,
    hash: currentConfigHash,
    pages,
    requiredEntitlements
  } = useFlowConfig();
  const {
    businessInfo: { businessEntityUuid },
    cartUuid: lastCartUuid,
    configHash: lastConfigHash,
    orderUuid,
    set
  } = useFlowState();

  if (authenticated && authPolicy === AUTH_POLICY.NOT_ALLOWED && !orderUuid) {
    // IMPORTANT: don't enforce this rule if there is an order for the flow.
    // This allows us to return to the flow after placing an order (and logging in).
    throw new Error(`Authenticated users are not allowed in ${flowId} flow.`);
  }

  // Check if user is missing any required entitlements
  let hasMissingEntitlements = false;
  if (authenticated && requiredEntitlements?.length > 0) {
    hasMissingEntitlements = !requiredEntitlements.every((requiredEntitlement) =>
      entitlements.has(requiredEntitlement)
    );

    if (hasMissingEntitlements) {
      captureException(`Authenticated user is missing required entitlements for ${flowId} flow.`, {
        level: 'warning'
      });
    }
  }

  const mustAuthenticate = !authenticated && authPolicy === AUTH_POLICY.REQUIRED;
  // IMPORTANT: don't enforce this rule if there is an order for the flow.
  // This allows us to return to the flow after placing an order (and logging in).
  const mustRedirectAuthenticated = authenticated && !!authRedirectPath && !orderUuid;
  const mustRedirect = mustRedirectAuthenticated || hasMissingEntitlements;
  const isFlowChanged = currentConfigHash !== lastConfigHash;
  // IMPORTANT: don't enforce this rule if there is an order for the flow.
  // This allows us to return to the flow after placing an order (and getting a new cart).
  const isCartNew = !lastCartUuid;
  const isCartChanged = !isCartNew && currentCartUuid !== lastCartUuid && !orderUuid;

  const loginPath = `${pathname}${search}`;
  const firstPage = pageUtils.getFirstPage(pages);

  // Enforce redirecting authenticated users
  useAuthRedirectHandler({
    businessEntityUuid,
    redirectTo: authRedirectOverrides[flowPage] ?? authRedirectPath,
    shouldRedirect: mustRedirectAuthenticated
  });

  // Enforce authentication
  useEffect(() => {
    if (!mustAuthenticate) {
      return;
    }

    login(loginPath);
  }, [login, loginPath, mustAuthenticate]);

  // Enforce missing entitlements
  useEffect(() => {
    if (!hasMissingEntitlements) {
      return;
    }

    window.location.href = DASHBOARD;
  }, [hasMissingEntitlements]);

  // Handle flow config or cart changes
  useEffect(() => {
    if (mustAuthenticate || mustRedirect) {
      return;
    }
    if (isFlowChanged) {
      set('configHash', currentConfigHash);
    }
    if (isCartChanged || isCartNew) {
      set('cartUuid', currentCartUuid);
    }
    // Reset progress in the flow
    if (isFlowChanged || isCartChanged) {
      set('lastPage', firstPage);
    }
  }, [
    currentCartUuid,
    currentConfigHash,
    isFlowChanged,
    isCartChanged,
    isCartNew,
    firstPage,
    mustAuthenticate,
    mustRedirect,
    navigate,
    set
  ]);

  if (mustRedirect) {
    return <Empty redirect-required />;
  }

  if (mustAuthenticate) {
    return <Empty authentication-required />;
  }

  return children || <Outlet />;
}
